import { IonContent, IonPage } from "@ionic/react";
import { SidebarMenu } from "components";
import React, { useRef, useState, useEffect } from "react";
import { SellersOrders } from "../components/SellersOrders";
import { CustomersVisitControl } from "../components/CustomersVisitControl";
import { formatDateToLocaleDateString, formatDateToISODateString, formatISODateStringToDate } from "utils";

// ====== Subcomponente local: Control único de fechas (sin Ionic) ======
function DateRangeControl({
  systemMin,
  today,
  uiStart,
  setUiStart,
  uiEnd,
  setUiEnd,
  onApply,
}: {
  systemMin: Date; // p.ej. 2024-01-01
  today: Date;     // hoy @ 00:00
  uiStart: Date;   // estado UI (medianoche)
  setUiStart: (d: Date) => void;
  uiEnd: Date;     // estado UI (medianoche)
  setUiEnd: (d: Date) => void;
  onApply: () => void;
}) {
  const MAX_RANGE_DAYS = 44; // => ventana máxima inclusiva de 45 días
  const ONE_DAY = 24 * 60 * 60 * 1000;

  const toMidnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);

  function clampToSystem(date: Date) {
    let x = toMidnight(date);
    const min = toMidnight(systemMin);
    const max = toMidnight(today);
    if (x < min) x = min;
    if (x > max) x = max;
    return x;
  }

  function normalizeWithAnchor(start: Date, end: Date, anchor: "start" | "end") {
    let s = clampToSystem(start);
    let e = clampToSystem(end);

    if (anchor === "start") {
      // El inicio "manda": fin entre [s, min(s+44, hoy)]
      const maxEnd = new Date(s.getTime() + MAX_RANGE_DAYS * ONE_DAY);
      const cappedMaxEnd = maxEnd > today ? today : maxEnd;
      if (e < s) e = cappedMaxEnd;
      if (e > cappedMaxEnd) e = cappedMaxEnd;
    } else {
      // El fin "manda": inicio entre [max(systemMin, e-44), e]
      const minStart = new Date(e.getTime() - MAX_RANGE_DAYS * ONE_DAY);
      const cappedMinStart = minStart < systemMin ? systemMin : minStart;
      if (s > e) s = cappedMinStart;
      if (s < cappedMinStart) s = cappedMinStart;
    }

    return [s, e] as const;
  }

  const handleStartInput = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const raw = ev.target.value; // "YYYY-MM-DD"
    const picked = formatISODateStringToDate(raw, false);
    if (!picked) return;
    const [s, e] = normalizeWithAnchor(picked, uiEnd, "start");
    setUiStart(s);
    setUiEnd(e);
  };

  const handleEndInput = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const raw = ev.target.value; // "YYYY-MM-DD"
    const picked = formatISODateStringToDate(raw, false);
    if (!picked) return;
    const [s, e] = normalizeWithAnchor(uiStart, picked, "end");
    setUiStart(s);
    setUiEnd(e);
  };

  return (
    <div className="flex items-end gap-4">
      <div className="flex flex-col">
        <label className="text-sm font-semibold mb-1">Fecha de inicio</label>
        <input
          type="date"
          className="border rounded-md px-3 py-2"
          min={formatDateToISODateString(systemMin)}
          max={formatDateToISODateString(today)}
          value={formatDateToISODateString(uiStart)}
          onChange={handleStartInput}
        />
      </div>

      <div className="flex flex-col">
        <label className="text-sm font-semibold mb-1">Fecha de fin</label>
        <input
          type="date"
          className="border rounded-md px-3 py-2"
          min={formatDateToISODateString(systemMin)}
          max={formatDateToISODateString(today)}
          value={formatDateToISODateString(uiEnd)}
          onChange={handleEndInput}
        />
      </div>

      <button
        className="bg-primary text-white px-4 py-2 rounded-md font-semibold"
        onClick={onApply}
      >
        Aplicar
      </button>
    </div>
  );
}

export const SellersControlPage = () => {
  // Fechas base
  const systemMin = new Date(2024, 0, 1); // límite inferior del sistema
  const today = new Date(new Date().setHours(0, 0, 0, 0));
  const todayEnd = new Date(new Date().setHours(23, 59, 59, 999));

  // Mostrar/ocultar detalles
  const [showOrdersDetails, setShowOrdersDetails] = useState<boolean>(false);

  // ===== Fechas "commit" (las que usan los servicios) =====
  const [startDate, setStartDate] = useState<Date>(new Date(today));      // 00:00
  const [finishDate, setFinishDate] = useState<Date>(new Date(todayEnd)); // 23:59:59.999

  // ===== Fechas UI (para el picker) =====
  const [startDateSearchFilter, setStartDateSearchFilter] = useState<Date>(new Date(today)); // 00:00
  const [finishDateSearchFilter, setFinishDateSearchFilter] = useState<Date>(new Date(today)); // 00:00

  // Trigger de refetch para los hijos
  const [hasDatesChanged, setHasDatesChanged] = useState<number>(1);

  const contentRef = useRef<HTMLIonContentElement>(null);
  const [selectedVendors, setSelectedVendors] = useState<string[] | null>(null);
  const [loadSellersOrders, setLoadSellersOrders] = useState<boolean>(false);

  // Commit inicial (opcional)
  useEffect(() => {
    setStartDate(startDateSearchFilter);
    const e = new Date(finishDateSearchFilter);
    e.setHours(23, 59, 59, 999);
    setFinishDate(e);
  }, []); // eslint-disable-line

  const commitDates = () => {
    const s = new Date(startDateSearchFilter); // 00:00
    const e = new Date(finishDateSearchFilter); // luego EOD
    e.setHours(23, 59, 59, 999);

    const changed = s.getTime() !== startDate.getTime() || e.getTime() !== finishDate.getTime();
    if (changed) {
      setStartDate(s);
      setFinishDate(e);
      setHasDatesChanged((v) => v + 1);
    }
  };

  return (
    <IonPage>
      <IonContent ref={contentRef}>
        <div className="w-full h-screen grid grid-cols-12">
          <div className="col-span-2 border-r-2 border-light">
            <SidebarMenu pageName="sellers_control" />
          </div>

          <div className="w-full h-screen flex flex-col flex-grow col-span-10">
            {/* Header */}
            <div className="w-full flex justify-between items-center border-b-2 border-tertiary px-2 py-3">
              <div className="flex flex-col items-start gap-2">
                <p className="font-semibold text-lg">
                  Del {formatDateToLocaleDateString(startDate)} al {formatDateToLocaleDateString(finishDate)}
                </p>
                <div className="flex items-center gap-4">
                  <p className="text-primary font-bold text-lg">Control de Vendedores</p>
                  {showOrdersDetails ? (
                    <button
                      onClick={() => setShowOrdersDetails(false)}
                      className="bg-tertiary px-4 py-1 rounded-md font-semibold"
                    >
                      Ver resumen
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        setShowOrdersDetails(true);
                        setLoadSellersOrders(true); // Activar la carga del componente
                      }}
                      className="bg-tertiary px-4 py-1 rounded-md font-semibold"
                    >
                      Ver detalles
                    </button>
                  )}
                </div>

              </div>



              {/* ÚNICO control de fechas (sin Ionic) */}
              <DateRangeControl
                systemMin={systemMin}
                today={today}
                uiStart={startDateSearchFilter}
                setUiStart={setStartDateSearchFilter}
                uiEnd={finishDateSearchFilter}
                setUiEnd={setFinishDateSearchFilter}
                onApply={commitDates}
              />
            </div>

            {/* Hijo 1 */}
            <CustomersVisitControl
              showOrdersDetails={showOrdersDetails}
              contentRef={contentRef}
              today={today}

              // Fechas COMMIT (para servicios)
              startDate={startDate}
              setStartDate={setStartDate}
              finishDate={finishDate}
              setFinishDate={setFinishDate}

              // (Props legacy: ya no se usan dentro del hijo para UI)
              startDateSearchFilter={startDateSearchFilter}
              setStartDateSearchFilter={setStartDateSearchFilter}
              finishDateSearchFilter={finishDateSearchFilter}
              setFinishDateSearchFilter={setFinishDateSearchFilter}

              hasDatesChanged={hasDatesChanged}
              setHasDatesChanged={setHasDatesChanged}

              selectedVendors={selectedVendors}
              onChangeSelectedVendors={setSelectedVendors}
            />

              {/* Hijo 2 (aplicar el mismo patrón) */ }
            {loadSellersOrders && (
              < SellersOrders
                  showOrdersDetails={showOrdersDetails}
                  contentRef={contentRef}
                  today={today}

                  startDate={startDate}
                  setStartDate={setStartDate}
                  finishDate={finishDate}
                  setFinishDate={setFinishDate}

                  startDateSearchFilter={startDateSearchFilter}
                  setStartDateSearchFilter={setStartDateSearchFilter}
                  finishDateSearchFilter={finishDateSearchFilter}
                  setFinishDateSearchFilter={setFinishDateSearchFilter}

                  hasDatesChanged={hasDatesChanged}
                  setHasDatesChanged={setHasDatesChanged}

                  selectedVendors={selectedVendors}
                  onChangeSelectedVendors={setSelectedVendors}
                />

            )}

          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};
